/**
 * Utilidades para el modelo de horas (horas + minutos, totales en minutos, bloques de 15).
 * Backend redondea hacia abajo a múltiplos de 15. Retiro 1.5% sobre horas aprobadas.
 */

/**
 * Minutos a texto "Xh Ym" o "X min" (formato doc).
 * @param {number} minutos - Total en minutos (puede ser 0)
 * @returns {string} Ej: "2h 30 min", "1h", "45 min", "0 min"
 */
export function minutosATexto(minutos) {
  if (minutos == null || isNaN(minutos)) return '0 min';
  const m = Math.round(Number(minutos));
  if (m <= 0) return '0 min';
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min} min`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min} min`;
}

/**
 * @deprecated Usar minutosATexto. Mantenido por compatibilidad.
 */
export function minutosToReadable(minutos) {
  if (minutos == null || isNaN(minutos)) return '0 h';
  const m = Math.round(Number(minutos));
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min} m`;
  if (min === 0) return `${h} h`;
  return `${h} h ${min} m`;
}

/**
 * Unidades hora (ej. 3.375) a texto legible con horas, minutos y segundos (sin redondear).
 * @param {number} horasUnidad - Valor en unidades de hora (totalDisponibleRetiro)
 * @returns {string} Ej: "3h 22m 30s", "0h 2m 15s"
 */
export function horasDisponiblesRetiroATexto(horasUnidad) {
  if (horasUnidad == null || isNaN(horasUnidad)) return '0 min';
  const h = Number(horasUnidad);
  if (h <= 0) return '0 min';
  const horas = Math.floor(h);
  const restoHoras = (h - horas) * 60; // minutos con decimales
  const minutos = Math.floor(restoHoras);
  const segundos = Math.round((restoHoras - minutos) * 60);
  const parts = [];
  if (horas > 0) parts.push(`${horas}h`);
  if (minutos > 0 || horas > 0) parts.push(`${minutos}m`);
  if (segundos > 0 || (horas === 0 && minutos === 0)) parts.push(`${segundos}s`);
  return parts.join(' ') || '0 min';
}

/**
 * Redondeo hacia abajo al bloque de 15 min (previsión front, mismo criterio que backend).
 * Tope máximo 300 min (5h).
 * @param {number} totalMinutos
 * @returns {number}
 */
export function redondearABloque15(totalMinutos) {
  if (totalMinutos <= 0) return 0;
  const bloque = Math.floor(Number(totalMinutos) / 15) * 15;
  return Math.min(bloque, 300);
}

/**
 * Calcular 1.5% de minutos (disponible retiro) en unidades hora.
 * @param {number} minutos
 * @returns {number}
 */
export function calcularDisponibleRetiro(minutos) {
  if (minutos == null || isNaN(minutos)) return 0;
  return (Number(minutos) / 60) * 0.015;
}

/**
 * Convierte minutos a horas decimales para mostrar "X.X h".
 * @param {number} minutos
 * @returns {number} Horas con decimales
 */
export function minutosToHorasDecimal(minutos) {
  if (minutos == null || isNaN(minutos)) return 0;
  return Math.round((Number(minutos) / 60) * 10) / 10;
}

/**
 * Obtiene los minutos a mostrar para una hora (respuesta API).
 * Prioriza horasCalculadas; si no existe, calcula desde horasIngresadasHoras + minutos.
 * @param {object} hora - Objeto Hora del API
 * @returns {number} Minutos
 */
export function getMinutosFromHora(hora) {
  if (!hora) return 0;
  if (hora.horasCalculadas != null && !isNaN(hora.horasCalculadas)) return Number(hora.horasCalculadas);
  const h = hora.horasIngresadasHoras != null ? Number(hora.horasIngresadasHoras) : 0;
  const m = hora.horasIngresadasMinutos != null ? Number(hora.horasIngresadasMinutos) : 0;
  return h * 60 + m;
}

/**
 * Texto corto de lo ingresado por el usuario: "Xh Ym".
 * @param {object} hora - Objeto Hora (horasIngresadasHoras, horasIngresadasMinutos)
 * @returns {string}
 */
export function formatoIngresado(hora) {
  if (!hora) return '—';
  const h = hora.horasIngresadasHoras != null ? Number(hora.horasIngresadasHoras) : 0;
  const m = hora.horasIngresadasMinutos != null ? Number(hora.horasIngresadasMinutos) : 0;
  if (h === 0 && m === 0) return '0 min';
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m} min`;
}
